import { useEffect, useState } from 'react';
import {
  formatMinorUsd,
  parseUsdToMinor,
  type AdminCreditNote,
  type AdminInvoice,
  type AdminPayment,
  type PaymentMethod,
} from '@fois-utsavvoice/shared';
import { ApiError } from '../api/client.js';
import {
  createCreditNote,
  getCreditNotePdfUrl,
  listInvoiceCreditNotes,
  listInvoicePayments,
  recordInvoicePayment,
  voidInvoicePayment,
  voidIssuedInvoice,
} from '../api/invoices.js';

const MANUAL_PAYMENT_METHODS: PaymentMethod[] = ['ZELLE', 'CASH', 'CARD', 'OTHER'];

function idempotencyKey(storageKey: string, refresh = false): string {
  if (!refresh) {
    const existing = sessionStorage.getItem(storageKey);
    if (existing) {
      return existing;
    }
  }
  const key = crypto.randomUUID();
  sessionStorage.setItem(storageKey, key);
  return key;
}

export function InvoicePaymentsPanel({
  invoice,
  accessToken,
  onInvoiceChange,
  onError,
}: {
  invoice: AdminInvoice;
  accessToken: string;
  onInvoiceChange: (invoice: AdminInvoice) => void;
  onError: (message: string | undefined) => void;
}) {
  const [payments, setPayments] = useState<AdminPayment[]>([]);
  const [creditNotes, setCreditNotes] = useState<AdminCreditNote[]>([]);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('ZELLE');
  const [voidReason, setVoidReason] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [invoiceVoidReason, setInvoiceVoidReason] = useState('');
  const [busy, setBusy] = useState<string | undefined>();

  const closed = invoice.status === 'VOID' || invoice.status === 'CREDITED';
  const canPay = !closed && invoice.balanceDueMinor > 0;
  const canVoidInvoice =
    !closed && invoice.paidMinor === 0 && ['ISSUED', 'SENT', 'OVERDUE'].includes(invoice.status);
  const remainingUncredited = invoice.totalMinor - invoice.creditedMinor;

  useEffect(() => {
    Promise.all([
      listInvoicePayments(accessToken, invoice.invoiceId),
      listInvoiceCreditNotes(accessToken, invoice.invoiceId),
    ])
      .then(([paymentPage, creditPage]) => {
        setPayments(paymentPage.items);
        setCreditNotes(creditPage.items);
      })
      .catch(() => onError('Payments could not be loaded.'));
  }, [accessToken, invoice.invoiceId, invoice.paidMinor, invoice.creditedMinor, invoice.status, onError]);

  async function onRecordPayment(): Promise<void> {
    onError(undefined);
    setBusy('pay');
    try {
      const amountMinor = parseUsdToMinor(amount, 'payment');
      const storageKey = `invoice-pay:${invoice.invoiceId}`;
      const result = await recordInvoicePayment(
        accessToken,
        invoice.invoiceId,
        { amountMinor, method },
        idempotencyKey(storageKey),
      );
      sessionStorage.removeItem(storageKey);
      setAmount('');
      setPayments((current) => [...current, result.payment]);
      onInvoiceChange(result.invoice);
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : 'The payment could not be recorded.');
    } finally {
      setBusy(undefined);
    }
  }

  async function onVoidPayment(payment: AdminPayment): Promise<void> {
    if (!voidReason.trim()) {
      onError('A reason is required to void a payment.');
      return;
    }
    onError(undefined);
    setBusy(`void-pay:${payment.paymentId}`);
    try {
      const storageKey = `invoice-void-pay:${payment.paymentId}`;
      const result = await voidInvoicePayment(
        accessToken,
        payment.paymentId,
        voidReason.trim(),
        idempotencyKey(storageKey),
      );
      sessionStorage.removeItem(storageKey);
      setPayments((current) =>
        current.map((item) => (item.paymentId === result.payment.paymentId ? result.payment : item)),
      );
      onInvoiceChange(result.invoice);
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : 'The payment could not be voided.');
    } finally {
      setBusy(undefined);
    }
  }

  async function onCreditNote(): Promise<void> {
    onError(undefined);
    setBusy('credit');
    try {
      const amountMinor = parseUsdToMinor(creditAmount, 'credit');
      const storageKey = `invoice-credit:${invoice.invoiceId}`;
      const result = await createCreditNote(
        accessToken,
        invoice.invoiceId,
        { amountMinor, reason: creditReason.trim() },
        idempotencyKey(storageKey),
      );
      sessionStorage.removeItem(storageKey);
      setCreditAmount('');
      setCreditReason('');
      setCreditNotes((current) => [...current, result.creditNote]);
      onInvoiceChange(result.invoice);
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : 'The credit note could not be issued.');
    } finally {
      setBusy(undefined);
    }
  }

  async function onVoidInvoice(): Promise<void> {
    if (!invoiceVoidReason.trim()) {
      onError('A reason is required to void this invoice.');
      return;
    }
    onError(undefined);
    setBusy('void-invoice');
    try {
      const storageKey = `invoice-void:${invoice.invoiceId}`;
      const updated = await voidIssuedInvoice(
        accessToken,
        invoice.invoiceId,
        invoiceVoidReason.trim(),
        idempotencyKey(storageKey),
      );
      onInvoiceChange(updated);
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : 'The invoice could not be voided.');
    } finally {
      setBusy(undefined);
    }
  }

  async function onDownloadCreditNote(creditNoteId: string): Promise<void> {
    setBusy(`cn-pdf:${creditNoteId}`);
    try {
      const signed = await getCreditNotePdfUrl(accessToken, creditNoteId);
      window.open(signed.url, '_blank', 'noopener,noreferrer');
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : 'The credit note PDF could not be opened.');
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <section className="mt-8 space-y-6 border-t border-linen pt-6">
      <h2 className="heading-lg text-crimson">Payments and corrections</h2>
      <dl className="grid gap-2 text-sm sm:grid-cols-3">
        <div className="flex justify-between gap-3 sm:block">
          <dt className="text-charcoal/70">Payments received</dt>
          <dd className="font-semibold">{formatMinorUsd(invoice.paidMinor)}</dd>
        </div>
        <div className="flex justify-between gap-3 sm:block">
          <dt className="text-charcoal/70">Credits applied</dt>
          <dd className="font-semibold">{formatMinorUsd(invoice.creditedMinor)}</dd>
        </div>
        <div className="flex justify-between gap-3 sm:block">
          <dt className="text-charcoal/70">Balance due</dt>
          <dd className="font-bold text-crimson">{formatMinorUsd(invoice.balanceDueMinor)}</dd>
        </div>
      </dl>

      {canPay ? (
        <div className="space-y-3">
          <h3 className="font-semibold text-charcoal">Record payment</h3>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="field-label" htmlFor="payment-amount">
                Amount
              </label>
              <input
                id="payment-amount"
                className="field-input"
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="payment-method">
                Method
              </label>
              <select
                id="payment-method"
                className="field-input"
                value={method}
                onChange={(event) => setMethod(event.target.value as PaymentMethod)}
              >
                {MANUAL_PAYMENT_METHODS.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                className="btn-primary"
                type="button"
                disabled={Boolean(busy) || !amount}
                onClick={() => void onRecordPayment()}
              >
                {busy === 'pay' ? 'Recording…' : 'Record payment'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div>
        <h3 className="font-semibold text-charcoal">Payment history</h3>
        {payments.length === 0 ? (
          <p className="mt-2 text-sm text-charcoal/80">No payments recorded.</p>
        ) : (
          <ul className="mt-2 space-y-2 text-sm">
            {payments.map((payment) => (
              <li
                key={payment.paymentId}
                className="flex flex-wrap items-center justify-between gap-2 border border-linen px-3 py-2"
              >
                <span>
                  {formatMinorUsd(payment.amountMinor)} · {payment.method} · {payment.status}
                </span>
                {payment.status === 'RECORDED' && !closed ? (
                  <button
                    className="btn-secondary"
                    type="button"
                    disabled={Boolean(busy)}
                    onClick={() => void onVoidPayment(payment)}
                  >
                    {busy === `void-pay:${payment.paymentId}` ? 'Voiding…' : 'Void payment'}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {payments.some((item) => item.status === 'RECORDED') && !closed ? (
          <div className="mt-3">
            <label className="field-label" htmlFor="void-payment-reason">
              Reason to void a payment
            </label>
            <input
              id="void-payment-reason"
              className="field-input"
              value={voidReason}
              onChange={(event) => setVoidReason(event.target.value)}
            />
          </div>
        ) : null}
      </div>

      {remainingUncredited > 0 && invoice.status !== 'VOID' ? (
        <div className="space-y-3">
          <h3 className="font-semibold text-charcoal">Issue credit note</h3>
          <p className="text-sm text-charcoal/80">
            Credit notes are for corrections. A paid invoice cannot be voided; issue a credit note
            instead. Remaining uncredited: {formatMinorUsd(remainingUncredited)}.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="field-label" htmlFor="credit-amount">
                Credit amount
              </label>
              <input
                id="credit-amount"
                className="field-input"
                inputMode="decimal"
                value={creditAmount}
                onChange={(event) => setCreditAmount(event.target.value)}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="credit-reason">
                Reason
              </label>
              <input
                id="credit-reason"
                className="field-input"
                value={creditReason}
                onChange={(event) => setCreditReason(event.target.value)}
              />
            </div>
          </div>
          <button
            className="btn-secondary"
            type="button"
            disabled={Boolean(busy) || !creditAmount || !creditReason.trim()}
            onClick={() => void onCreditNote()}
          >
            {busy === 'credit' ? 'Issuing…' : 'Issue credit note'}
          </button>
        </div>
      ) : null}

      {creditNotes.length > 0 ? (
        <div>
          <h3 className="font-semibold text-charcoal">Credit notes</h3>
          <ul className="mt-2 space-y-2 text-sm">
            {creditNotes.map((note) => (
              <li
                key={note.creditNoteId}
                className="flex flex-wrap items-center justify-between gap-2 border border-linen px-3 py-2"
              >
                <span>
                  {note.creditNoteNumber} · {formatMinorUsd(note.amountMinor)}
                </span>
                <button
                  className="btn-secondary"
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => void onDownloadCreditNote(note.creditNoteId)}
                >
                  {busy === `cn-pdf:${note.creditNoteId}` ? 'Opening…' : 'Download PDF'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {canVoidInvoice ? (
        <div className="space-y-3">
          <h3 className="font-semibold text-charcoal">Void invoice</h3>
          <p className="text-sm text-charcoal/80">
            Voiding keeps the invoice number retired. This is only allowed when no valid payment
            exists.
          </p>
          <label className="field-label" htmlFor="void-invoice-reason">
            Reason
          </label>
          <input
            id="void-invoice-reason"
            className="field-input"
            value={invoiceVoidReason}
            onChange={(event) => setInvoiceVoidReason(event.target.value)}
          />
          <button
            className="btn-secondary"
            type="button"
            disabled={Boolean(busy) || !invoiceVoidReason.trim()}
            onClick={() => void onVoidInvoice()}
          >
            {busy === 'void-invoice' ? 'Voiding…' : 'Void invoice'}
          </button>
        </div>
      ) : null}
    </section>
  );
}
